import { useState, useCallback } from 'react';
import type React from 'react';
import { isSetsunaMagic } from '../../engine/keywordAbilities';
import { continuousEffectEngine } from '../../engine/continuousEffects';
import { executeSkillEffects } from '../../engine/effectEngine';
import { CHAIN_POINT_NAMES } from '../../engine/keywordAbilities';
import type { MonsterInstance, HandCard, CardWithId, CardData, FieldCardInstance } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import type {
  ChainConfirmation,
  ChainPoint,
  PendingSetsunaAction,
  PendingMonsterTarget,
  PendingHandSelection,
  PendingDeckReview,
} from '../../types/ui';

interface UseSetsunaMagicDeps {
  // ゲーム状態（読み取り専用）
  currentPlayer: PlayerNumber;
  p1Hand: HandCard[];
  p2Hand: HandCard[];
  p1ActiveSP: number;
  p2ActiveSP: number;
  p1Field: (MonsterInstance | null)[];
  p2Field: (MonsterInstance | null)[];
  p1Life: number;
  p2Life: number;
  p1Deck: CardWithId[];
  p2Deck: CardWithId[];
  p1Graveyard: CardWithId[];
  p2Graveyard: CardWithId[];
  p1MagicBlocked: boolean;
  p2MagicBlocked: boolean;

  // 外部関数
  addLog: (message: string, type?: 'info' | 'damage' | 'heal') => void;

  // SP/手札/墓地操作
  setP1ActiveSP: React.Dispatch<React.SetStateAction<number>>;
  setP2ActiveSP: React.Dispatch<React.SetStateAction<number>>;
  setP1RestedSP: React.Dispatch<React.SetStateAction<number>>;
  setP2RestedSP: React.Dispatch<React.SetStateAction<number>>;
  setP1Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP2Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP1Graveyard: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP2Graveyard: React.Dispatch<React.SetStateAction<CardWithId[]>>;

  // 魔法効果実行に必要な追加 setter
  setP1Life: React.Dispatch<React.SetStateAction<number>>;
  setP2Life: React.Dispatch<React.SetStateAction<number>>;
  setP1Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP2Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP1Deck: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP2Deck: React.Dispatch<React.SetStateAction<CardWithId[]>>;

  // UI選択制御（useBattleSelection から）
  setPendingMonsterTarget: React.Dispatch<React.SetStateAction<PendingMonsterTarget | null>>;
  setPendingHandSelection: React.Dispatch<React.SetStateAction<PendingHandSelection | null>>;
  setPendingDeckReview: React.Dispatch<React.SetStateAction<PendingDeckReview | null>>;
}

interface UseSetsunaMagicReturn {
  // State
  setsunaPendingActivation: boolean;
  setsunaPendingCard: HandCard | null;
  chainConfirmation: ChainConfirmation | null;
  pendingSetsunaAction: PendingSetsunaAction | null;

  // Setters
  setSetsunaPendingActivation: React.Dispatch<React.SetStateAction<boolean>>;
  setSetsunaPendingCard: React.Dispatch<React.SetStateAction<HandCard | null>>;
  setChainConfirmation: React.Dispatch<React.SetStateAction<ChainConfirmation | null>>;
  setPendingSetsunaAction: React.Dispatch<React.SetStateAction<PendingSetsunaAction | null>>;

  // チェーン確認API
  startChainConfirmation: (chainPoint: ChainPoint, pendingAction: PendingSetsunaAction, context?: Record<string, unknown>) => boolean;
  skipChainConfirmation: () => void;
  activateSetsunaInChain: (card: HandCard) => void;
  cancelChainAndAction: () => void;

  // 刹那詠唱API
  canUseSetsunaMagic: () => boolean;
  getSetsunaMagicsForNonActivePlayer: () => HandCard[];
  activateSetsunaMagic: (card: HandCard) => boolean;
  getSetsunaActualCost: (card: HandCard | null, playerNum: PlayerNumber) => number;
  filterActivatableSetsunaCards: (hand: HandCard[], activeSP: number, playerNum: PlayerNumber) => HandCard[];

  // UI制御
  startSetsunaMagicMode: () => void;
  cancelSetsunaMagicMode: () => void;
  selectSetsunaMagicCard: (card: HandCard) => void;

  // Reset
  resetAll: () => void;
}

/**
 * useSetsunaMagic - 刹那詠唱とチェーンポイントシステムを管理するHook
 * Phase 4-1: magic-spirit.jsx から刹那詠唱関連の state と関数を集約
 *
 * NOTE: pendingSetsunaAction の監視 useEffect は magic-spirit.jsx に残す
 * （executeAttack への依存を避けるため）
 */
export const useSetsunaMagic = ({
  // ゲーム状態（読み取り専用）
  currentPlayer,
  p1Hand, p2Hand,
  p1ActiveSP, p2ActiveSP,
  p1Field, p2Field,
  p1Life, p2Life,
  p1Deck, p2Deck,
  p1Graveyard, p2Graveyard,
  p1MagicBlocked, p2MagicBlocked,

  // 外部関数
  addLog,

  // SP/手札/墓地操作
  setP1ActiveSP, setP2ActiveSP,
  setP1RestedSP, setP2RestedSP,
  setP1Hand, setP2Hand,
  setP1Graveyard, setP2Graveyard,

  // 魔法効果実行に必要な追加 setter
  setP1Life, setP2Life,
  setP1Field, setP2Field,
  setP1Deck, setP2Deck,

  // UI選択制御（useBattleSelection から）
  setPendingMonsterTarget,
  setPendingHandSelection,
  setPendingDeckReview,
}: UseSetsunaMagicDeps): UseSetsunaMagicReturn => {
  // ========================================
  // State
  // ========================================
  const [setsunaPendingActivation, setSetsunaPendingActivation] = useState<boolean>(false);
  const [setsunaPendingCard, setSetsunaPendingCard] = useState<HandCard | null>(null);
  const [chainConfirmation, setChainConfirmation] = useState<ChainConfirmation | null>(null);
  const [pendingSetsunaAction, setPendingSetsunaAction] = useState<PendingSetsunaAction | null>(null);

  // ========================================
  // 内部ユーティリティ
  // ========================================

  /**
   * 継続効果を考慮した刹那詠唱コストを計算（UI表示用）
   */
  const getSetsunaActualCost = useCallback((card: HandCard | null, playerNum: PlayerNumber): number => {
    if (!card) return 0;
    const baseCost = card.cost || 0;
    const magicCostContext = {
      effectOwner: playerNum,
      currentPlayer: playerNum,
      p1Field,
      p2Field,
      p1Life,
      p2Life,
    };
    // TargetCard型互換性のためキャスト
    const costModifier = continuousEffectEngine.calculateMagicCostModifier(
      card as unknown as { uniqueId?: string; owner?: PlayerNumber; [key: string]: unknown },
      playerNum,
      magicCostContext
    );
    return Math.max(0, baseCost + costModifier);
  }, [p1Field, p2Field, p1Life, p2Life]);

  /**
   * 継続効果を考慮して刹那詠唱発動可能カードをフィルタする共通関数
   */
  const filterActivatableSetsunaCards = useCallback((hand: HandCard[], activeSP: number, playerNum: PlayerNumber): HandCard[] => {
    const magicCostContext = {
      effectOwner: playerNum,
      currentPlayer: playerNum,
      p1Field,
      p2Field,
      p1Life,
      p2Life,
    };

    return hand.filter(card => {
      if (!isSetsunaMagic(card)) return false;
      const baseCost = card.cost || 0;
      // TargetCard型互換性のためキャスト
      const costModifier = continuousEffectEngine.calculateMagicCostModifier(
        card as unknown as { uniqueId?: string; owner?: PlayerNumber; [key: string]: unknown },
        playerNum,
        magicCostContext
      );
      const actualCost = Math.max(0, baseCost + costModifier);
      return activeSP >= actualCost;
    });
  }, [p1Field, p2Field, p1Life, p2Life]);

  /**
   * 非アクティブプレイヤーが刹那詠唱可能かチェック（内部用）
   */
  const canNonActivePlayerUseSetsuna = useCallback((): boolean => {
    const nonActivePlayer: PlayerNumber = currentPlayer === 1 ? 2 : 1;
    const hand = nonActivePlayer === 1 ? p1Hand : p2Hand;
    const activeSP = nonActivePlayer === 1 ? p1ActiveSP : p2ActiveSP;
    const activatableCards = filterActivatableSetsunaCards(hand, activeSP, nonActivePlayer);
    return activatableCards.length > 0;
  }, [currentPlayer, p1Hand, p2Hand, p1ActiveSP, p2ActiveSP, filterActivatableSetsunaCards]);

  // ========================================
  // 公開API - 刹那詠唱
  // ========================================

  /**
   * 相手ターン中に刹那詠唱を発動可能か判定
   */
  const canUseSetsunaMagic = useCallback((): boolean => {
    const nonActivePlayer: PlayerNumber = currentPlayer === 1 ? 2 : 1;

    // 魔法カード使用制限チェック
    const isMagicBlocked = nonActivePlayer === 1 ? p1MagicBlocked : p2MagicBlocked;
    if (isMagicBlocked) {
      return false;
    }

    const hand = nonActivePlayer === 1 ? p1Hand : p2Hand;
    const activeSP = nonActivePlayer === 1 ? p1ActiveSP : p2ActiveSP;

    const activatableCards = filterActivatableSetsunaCards(hand, activeSP, nonActivePlayer);
    return activatableCards.length > 0;
  }, [currentPlayer, p1Hand, p2Hand, p1ActiveSP, p2ActiveSP, p1MagicBlocked, p2MagicBlocked, filterActivatableSetsunaCards]);

  /**
   * 刹那詠唱発動可能カード一覧を取得
   */
  const getSetsunaMagicsForNonActivePlayer = useCallback((): HandCard[] => {
    const nonActivePlayer: PlayerNumber = currentPlayer === 1 ? 2 : 1;

    const isMagicBlocked = nonActivePlayer === 1 ? p1MagicBlocked : p2MagicBlocked;
    if (isMagicBlocked) {
      return [];
    }

    const hand = nonActivePlayer === 1 ? p1Hand : p2Hand;
    const activeSP = nonActivePlayer === 1 ? p1ActiveSP : p2ActiveSP;

    return filterActivatableSetsunaCards(hand, activeSP, nonActivePlayer);
  }, [currentPlayer, p1Hand, p2Hand, p1ActiveSP, p2ActiveSP, p1MagicBlocked, p2MagicBlocked, filterActivatableSetsunaCards]);

  /**
   * 刹那詠唱カードを発動
   */
  const activateSetsunaMagic = useCallback((card: HandCard): boolean => {
    if (!card || !isSetsunaMagic(card)) {
      addLog('刹那詠唱カードではありません', 'damage');
      return false;
    }

    const castingPlayer: PlayerNumber = currentPlayer === 1 ? 2 : 1;

    const isMagicBlocked = castingPlayer === 1 ? p1MagicBlocked : p2MagicBlocked;
    if (isMagicBlocked) {
      addLog('魔法カードを使用できません！（触覚持ち粘液獣の効果）', 'damage');
      return false;
    }

    const baseCost = card.cost || 0;
    const magicCostContext = {
      effectOwner: castingPlayer,
      currentPlayer: castingPlayer,
      p1Field,
      p2Field,
      p1Life,
      p2Life,
    };
    // TargetCard型互換性のためキャスト
    const magicCostModifier = continuousEffectEngine.calculateMagicCostModifier(
      card as unknown as { uniqueId?: string; owner?: PlayerNumber; [key: string]: unknown },
      castingPlayer,
      magicCostContext
    );
    const setsunaCost = Math.max(0, baseCost + magicCostModifier);
    const activeSP = castingPlayer === 1 ? p1ActiveSP : p2ActiveSP;

    if (activeSP < setsunaCost) {
      addLog(`SPが足りません！（刹那詠唱コスト: ${setsunaCost}, 現在: ${activeSP}）`, 'damage');
      return false;
    }

    // SPを消費
    if (castingPlayer === 1) {
      setP1ActiveSP(prev => prev - setsunaCost);
      setP1RestedSP(prev => prev + setsunaCost);
      setP1Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
      setP1Graveyard(prev => [...prev, card]);
    } else {
      setP2ActiveSP(prev => prev - setsunaCost);
      setP2RestedSP(prev => prev + setsunaCost);
      setP2Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
      setP2Graveyard(prev => [...prev, card]);
    }

    const costLog = magicCostModifier < 0
      ? `（基本コスト${baseCost}${magicCostModifier}=${setsunaCost}）`
      : `（コスト${setsunaCost}）`;
    addLog(`プレイヤー${castingPlayer}: 【刹那詠唱】${card.name}を発動！${costLog}`, 'info');

    // 魔法効果を実行
    if (card.effect) {
      const context = {
        currentPlayer: castingPlayer,
        monsterIndex: undefined as number | undefined,
        // Life
        p1Life,
        p2Life,
        setP1Life,
        setP2Life,
        // Field
        p1Field,
        p2Field,
        setP1Field,
        setP2Field,
        // Hand
        p1Hand,
        p2Hand,
        setP1Hand,
        setP2Hand,
        // Deck
        p1Deck,
        p2Deck,
        setP1Deck,
        setP2Deck,
        // Graveyard
        p1Graveyard,
        p2Graveyard,
        setP1Graveyard,
        setP2Graveyard,
        // SP
        p1ActiveSP,
        p2ActiveSP,
        setP1ActiveSP,
        setP2ActiveSP,
        p1RestedSP: 0, // 刹那詠唱では直接操作しないのでダミー値
        p2RestedSP: 0,
        setP1RestedSP,
        setP2RestedSP,
        // FieldCard (刹那詠唱hookにはないためnullを設定)
        p1FieldCard: null as FieldCardInstance | CardData | null,
        p2FieldCard: null as FieldCardInstance | CardData | null,
        setP1FieldCard: (() => {}) as React.Dispatch<React.SetStateAction<FieldCardInstance | CardData | null>>,
        setP2FieldCard: (() => {}) as React.Dispatch<React.SetStateAction<FieldCardInstance | CardData | null>>,
        // Utilities
        addLog,
        setPendingDeckReview,
        setPendingMonsterTarget,
        setPendingHandSelection,
      };
      executeSkillEffects(card.effect, context, card.id);
    }

    setSetsunaPendingActivation(false);
    setSetsunaPendingCard(null);

    return true;
  }, [currentPlayer, p1ActiveSP, p2ActiveSP, p1Field, p2Field, p1Hand, p2Hand,
      p1Deck, p2Deck, p1Graveyard, p2Graveyard, p1Life, p2Life,
      p1MagicBlocked, p2MagicBlocked, addLog,
      setP1ActiveSP, setP2ActiveSP, setP1RestedSP, setP2RestedSP,
      setP1Hand, setP2Hand, setP1Graveyard, setP2Graveyard,
      setP1Life, setP2Life, setP1Field, setP2Field, setP1Deck, setP2Deck,
      setPendingMonsterTarget, setPendingHandSelection, setPendingDeckReview]);

  // ========================================
  // UI制御
  // ========================================

  const startSetsunaMagicMode = useCallback((): void => {
    setSetsunaPendingActivation(true);
    setSetsunaPendingCard(null);
    addLog('刹那詠唱カードを選択してください', 'info');
  }, [addLog]);

  const cancelSetsunaMagicMode = useCallback((): void => {
    setSetsunaPendingActivation(false);
    setSetsunaPendingCard(null);
  }, []);

  const selectSetsunaMagicCard = useCallback((card: HandCard): void => {
    if (setsunaPendingCard && setsunaPendingCard.uniqueId === card.uniqueId) {
      activateSetsunaMagic(card);
    } else {
      setSetsunaPendingCard(card);
    }
  }, [setsunaPendingCard, activateSetsunaMagic]);

  // ========================================
  // チェーン確認システム
  // ========================================

  /**
   * チェーンポイントで確認を開始
   * @returns {boolean} 確認が必要な場合true、不要な場合false
   */
  const startChainConfirmation = useCallback((chainPoint: ChainPoint, pendingAction: PendingSetsunaAction, context: Record<string, unknown> = {}): boolean => {
    const nonActivePlayer: PlayerNumber = currentPlayer === 1 ? 2 : 1;

    if (!canNonActivePlayerUseSetsuna()) {
      return false;
    }

    setChainConfirmation({
      chainPoint,
      askingPlayer: nonActivePlayer,
      pendingAction,
      context,
    });

    const pointName = CHAIN_POINT_NAMES[chainPoint] || chainPoint;
    addLog(`【${pointName}】プレイヤー${nonActivePlayer}、刹那詠唱を発動しますか？`, 'info');

    return true;
  }, [currentPlayer, canNonActivePlayerUseSetsuna, addLog]);

  /**
   * チェーン確認をスキップ（発動しない）
   * pendingSetsunaAction を設定して、useEffect で攻撃を実行する
   */
  const skipChainConfirmation = useCallback((): void => {
    if (!chainConfirmation) return;

    const { pendingAction } = chainConfirmation;
    setChainConfirmation(null);

    // pendingSetsunaAction を設定して、magic-spirit.tsx の useEffect で攻撃を実行する
    if (pendingAction) {
      setPendingSetsunaAction(pendingAction);
    }
  }, [chainConfirmation]);

  /**
   * チェーン確認で刹那詠唱を発動する
   */
  const activateSetsunaInChain = useCallback((card: HandCard): void => {
    if (!chainConfirmation || !card) return;

    const { pendingAction, askingPlayer } = chainConfirmation;

    const baseCost = card.cost || 0;
    const magicCostContext = {
      effectOwner: askingPlayer,
      currentPlayer: askingPlayer,
      p1Field,
      p2Field,
      p1Life,
      p2Life,
    };
    // TargetCard型互換性のためキャスト
    const magicCostModifier = continuousEffectEngine.calculateMagicCostModifier(
      card as unknown as { uniqueId?: string; owner?: PlayerNumber; [key: string]: unknown },
      askingPlayer,
      magicCostContext
    );
    const setsunaCost = Math.max(0, baseCost + magicCostModifier);
    const activeSP = askingPlayer === 1 ? p1ActiveSP : p2ActiveSP;

    if (activeSP < setsunaCost) {
      addLog(`SPが足りません！（刹那詠唱コスト: ${setsunaCost}, 現在: ${activeSP}）`, 'damage');
      return;
    }

    // SPを消費
    if (askingPlayer === 1) {
      setP1ActiveSP(prev => prev - setsunaCost);
      setP1RestedSP(prev => prev + setsunaCost);
      setP1Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
      setP1Graveyard(prev => [...prev, card]);
    } else {
      setP2ActiveSP(prev => prev - setsunaCost);
      setP2RestedSP(prev => prev + setsunaCost);
      setP2Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
      setP2Graveyard(prev => [...prev, card]);
    }

    const costLog = magicCostModifier < 0
      ? `（基本コスト${baseCost}${magicCostModifier}=${setsunaCost}）`
      : `（コスト${setsunaCost}）`;
    addLog(`プレイヤー${askingPlayer}: 【刹那詠唱】${card.name}を発動！${costLog}`, 'info');

    // 魔法効果を実行
    if (card.effect) {
      interface AttackerInfo {
        attacker?: MonsterInstance | null;
        attackerIndex?: number;
        targetIndex?: number | null;
      }
      const attackerInfo: AttackerInfo = {};
      if (pendingAction.type === 'attack' && pendingAction.attackerIndex !== undefined) {
        const attackerField = currentPlayer === 1 ? p1Field : p2Field;
        attackerInfo.attacker = attackerField[pendingAction.attackerIndex];
        attackerInfo.attackerIndex = pendingAction.attackerIndex;
        attackerInfo.targetIndex = pendingAction.targetIndex;
      }

      const context = {
        currentPlayer: askingPlayer,
        monsterIndex: undefined as number | undefined,
        // Life
        p1Life,
        p2Life,
        setP1Life,
        setP2Life,
        // Field
        p1Field,
        p2Field,
        setP1Field,
        setP2Field,
        // Hand
        p1Hand,
        p2Hand,
        setP1Hand,
        setP2Hand,
        // Deck
        p1Deck,
        p2Deck,
        setP1Deck,
        setP2Deck,
        // Graveyard
        p1Graveyard,
        p2Graveyard,
        setP1Graveyard,
        setP2Graveyard,
        // SP
        p1ActiveSP,
        p2ActiveSP,
        setP1ActiveSP,
        setP2ActiveSP,
        p1RestedSP: 0, // 刹那詠唱では直接操作しないのでダミー値
        p2RestedSP: 0,
        setP1RestedSP,
        setP2RestedSP,
        // FieldCard (刹那詠唱hookにはないためnullを設定)
        p1FieldCard: null as FieldCardInstance | CardData | null,
        p2FieldCard: null as FieldCardInstance | CardData | null,
        setP1FieldCard: (() => {}) as React.Dispatch<React.SetStateAction<FieldCardInstance | CardData | null>>,
        setP2FieldCard: (() => {}) as React.Dispatch<React.SetStateAction<FieldCardInstance | CardData | null>>,
        // Utilities
        addLog,
        setPendingDeckReview,
        setPendingMonsterTarget,
        setPendingHandSelection,
        ...attackerInfo,
        chainContext: chainConfirmation.context,
      };
      executeSkillEffects(card.effect, context, card.id);
    }

    setChainConfirmation(null);
    setSetsunaPendingCard(null);

    // 保留中のアクションを設定（useEffectで実行される）
    setPendingSetsunaAction(pendingAction);
  }, [chainConfirmation, currentPlayer, p1ActiveSP, p2ActiveSP, p1Field, p2Field,
      p1Hand, p2Hand, p1Deck, p2Deck, p1Graveyard, p2Graveyard, p1Life, p2Life,
      addLog, setP1ActiveSP, setP2ActiveSP, setP1RestedSP, setP2RestedSP,
      setP1Hand, setP2Hand, setP1Graveyard, setP2Graveyard,
      setP1Life, setP2Life, setP1Field, setP2Field, setP1Deck, setP2Deck,
      setPendingMonsterTarget, setPendingHandSelection, setPendingDeckReview]);

  /**
   * チェーン確認をキャンセル（攻撃自体をキャンセル）
   */
  const cancelChainAndAction = useCallback((): void => {
    setChainConfirmation(null);
    setSetsunaPendingCard(null);
    addLog('行動がキャンセルされました', 'info');
  }, [addLog]);

  // ========================================
  // Reset
  // ========================================
  const resetAll = useCallback((): void => {
    setSetsunaPendingActivation(false);
    setSetsunaPendingCard(null);
    setChainConfirmation(null);
    setPendingSetsunaAction(null);
  }, []);

  // ========================================
  // Return
  // ========================================
  return {
    // State
    setsunaPendingActivation,
    setsunaPendingCard,
    chainConfirmation,
    pendingSetsunaAction,

    // Setters
    setSetsunaPendingActivation,
    setSetsunaPendingCard,
    setChainConfirmation,
    setPendingSetsunaAction,

    // チェーン確認API
    startChainConfirmation,
    skipChainConfirmation,
    activateSetsunaInChain,
    cancelChainAndAction,

    // 刹那詠唱API
    canUseSetsunaMagic,
    getSetsunaMagicsForNonActivePlayer,
    activateSetsunaMagic,
    getSetsunaActualCost,
    filterActivatableSetsunaCards,  // ChainConfirmationModal用

    // UI制御
    startSetsunaMagicMode,
    cancelSetsunaMagicMode,
    selectSetsunaMagicCard,

    // Reset
    resetAll,
  };
};

export default useSetsunaMagic;
